import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { JitaContextBadge } from '../components/JitaContextBadge'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { useJitaContext, type JitaContextEntry } from '../hooks/useJitaContext'
import { SellHereTab } from './SellHereTab'

type Mode = 'haul' | 'sell-here'

type Verdict = 'haul_jita' | 'insufficient_data'

type Advisory = {
  type_id: number
  name: string
  quantity: number
  is_ore: boolean
  jita: { sell_price: number | null }
  best_action: { verdict: Verdict; reason: string; delta_pct: number | null }
}

const VERDICT_FILTERS: Array<{ key: Verdict | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'haul_jita', label: 'Haul a Jita' },
  { key: 'insufficient_data', label: 'Sin datos' },
]

const PAGE_SIZE = 20

function verdictBadgeClasses(verdict: Verdict): string {
  switch (verdict) {
    case 'haul_jita':
      return 'bg-eve-success/10 border-eve-success text-eve-success'
    case 'insufficient_data':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
  }
}

function verdictLabel(verdict: Verdict): string {
  switch (verdict) {
    case 'haul_jita':
      return 'Haul a Jita'
    case 'insufficient_data':
      return 'Sin datos'
  }
}

export function Utility() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const [mode, setMode] = useState<Mode>('haul')

  if (mode === 'sell-here') {
    return (
      <div className="space-y-6">
        <UtilityHeader mode={mode} setMode={setMode} />
        <SellHereTab />
      </div>
    )
  }

  return <HaulAdvice mode={mode} setMode={setMode} forceKey={forceRefetchKey} force={force} bump={bump} />
}

function UtilityHeader({
  mode,
  setMode,
}: {
  mode: Mode
  setMode: (m: Mode) => void
}) {
  return (
    <div>
      <h1 className="text-2xl font-bold">Utility</h1>
      <p className="text-eve-muted text-sm mt-1">
        Decisiones de venta para tu hangar dockeado.
      </p>
      {/* Tab switcher — pill group, mismo estilo que el toggle del WalletTimeline */}
      <div className="mt-3 inline-flex gap-1 bg-eve-panel border border-eve-border rounded-md p-0.5">
        <button
          onClick={() => setMode('haul')}
          className={`px-3 py-1 text-xs font-mono rounded transition-colors ${
            mode === 'haul'
              ? 'bg-eve-accent text-eve-bg font-semibold'
              : 'text-eve-muted hover:text-eve-text'
          }`}
        >
          Vendé en Jita
        </button>
        <button
          onClick={() => setMode('sell-here')}
          className={`px-3 py-1 text-xs font-mono rounded transition-colors ${
            mode === 'sell-here'
              ? 'bg-eve-accent text-eve-bg font-semibold'
              : 'text-eve-muted hover:text-eve-text'
          }`}
        >
          Vendé acá
        </button>
      </div>
    </div>
  )
}

function HaulAdvice({
  mode,
  setMode,
  forceKey,
  force,
  bump,
}: {
  mode: Mode
  setMode: (m: Mode) => void
  forceKey: number
  force: boolean
  bump: () => void
}) {
  const [page, setPage] = useState(1)  // 1-indexed
  const offset = (page - 1) * PAGE_SIZE

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['utility-sell-advice', forceKey, offset],
    queryFn: () => api.utilitySellAdvice(force, offset, PAGE_SIZE),
  })

  const [verdictFilter, setVerdictFilter] = useState<Verdict | 'all'>('all')
  const [search, setSearch] = useState('')

  // Jita avg 30d — pill "−8% vs avg 30d" / "+12% vs avg 30d" por item.
  // Una sola request para la página actual (cap 30 en el backend).
  const pageItems = data?.items ?? []
  const jitaPricesByType = useMemo(() => {
    const m = new Map<number, number | null>()
    for (const it of pageItems) m.set(it.type_id, it.jita.sell_price)
    return m
  }, [pageItems])
  const { map: jitaCtxMap } = useJitaContext(
    pageItems.map((it) => it.type_id),
    jitaPricesByType,
  )

  const filtered = pageItems.filter((it) => {
    if (verdictFilter !== 'all' && it.best_action.verdict !== verdictFilter) return false
    if (search.trim() && !it.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const pageInfo = data?.page
  const totalPages = pageInfo?.total_pages ?? 1
  const totalItems = pageInfo?.total ?? 0
  const hasMore = pageInfo?.has_more ?? false
  const priced = data?.summary.priced ?? 0

  // Reset a página 1 si los filtros de la URL cambian (no aplica acá, pero
  // buen lugar para recordatorio).
  const goPrev = () => setPage((p) => Math.max(1, p - 1))
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1))

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <UtilityHeader mode={mode} setMode={setMode} />
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      <p className="text-eve-muted text-sm -mt-4">
        Vendé en Jita — precio de venta en Jita para tu hangar, auto-detectado.
        Páginas de {PAGE_SIZE} items.
      </p>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Location"
            value={
              data.location.kind === 'in_space'
                ? 'In space'
                : data.location.region_id
                  ? `Region ${data.location.region_id}`
                  : '—'
            }
            sub={
              data.location.kind === 'in_space'
                ? `System ${data.location.solar_system_id ?? '—'}`
                : `${data.location.station_id ? `Station ${data.location.station_id}` : ''} · ${data.location.kind}`
            }
          />
          <SummaryCard
            label="Items en hangar"
            value={String(totalItems)}
            sub={`${totalPages} páginas`}
          />
          <SummaryCard
            label="Si vendés esta página en Jita"
            value={`${formatISKShort(data.summary.total_value_if_followed)} ISK`}
            sub={`${priced} priced en esta view`}
          />
          <SummaryCard
            label="Filtrar"
            value=""
            sub={
              <select
                value={verdictFilter}
                onChange={(e) => setVerdictFilter(e.target.value as Verdict | 'all')}
                className="bg-eve-panel border border-eve-border rounded-md px-2 py-1 text-sm text-eve-text focus:border-eve-accent focus:outline-none w-full mt-1"
              >
                {VERDICT_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            }
          />
        </div>
      )}

      <div className="card">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar item…"
            className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
          />
          <span className="text-eve-muted text-xs ml-auto">
            {filtered.length} / {data?.items.length ?? 0}
            {totalItems > 0 && (
              <>
                {' '}· global {offset + 1}-{Math.min(offset + PAGE_SIZE, totalItems)} de {totalItems}
              </>
            )}
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : filtered.length === 0 ? (
          <div className="text-eve-muted py-8 text-center">
            {data?.items.length === 0
              ? 'No tenés items en el hangar.'
              : 'Sin matches para el filtro en esta página.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((it) => (
              <AdvisoryRow
                key={it.type_id}
                item={it}
                jitaCtx={jitaCtxMap.get(it.type_id)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between gap-4 pt-4 border-t border-eve-border">
            <button
              onClick={goPrev}
              disabled={page === 1 || isFetching}
              className="px-3 py-1.5 bg-eve-panel border border-eve-border rounded-md text-sm font-medium hover:bg-eve-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <span className="text-sm text-eve-muted">
              Página <span className="text-eve-text font-medium">{page}</span> de {totalPages}
            </span>
            <button
              onClick={goNext}
              disabled={!hasMore || isFetching}
              className="px-3 py-1.5 bg-eve-panel border border-eve-border rounded-md text-sm font-medium hover:bg-eve-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub: React.ReactNode
}) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      {value && <div className="text-lg font-semibold mt-0.5">{value}</div>}
      <div className="text-xs text-eve-muted mt-1">{sub}</div>
    </div>
  )
}

function AdvisoryRow({ item, jitaCtx }: { item: Advisory; jitaCtx?: JitaContextEntry }) {
  const verdict = item.best_action.verdict
  const jitaTotal = item.jita.sell_price !== null ? item.jita.sell_price * item.quantity : null

  return (
    <details className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-eve-muted font-mono">
              × {item.quantity.toLocaleString('en-US')}
            </span>
            {item.is_ore && (
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-accent/10 border border-eve-accent/30 text-eve-accent">
                ore
              </span>
            )}
            <JitaContextBadge
              delta={jitaCtx?.delta_pct ?? null}
              avg={jitaCtx?.avg_30d ?? null}
              current={jitaCtx?.current_price ?? null}
            />
          </div>
          <div className="text-xs text-eve-muted mt-1">
            {item.best_action.reason}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <PricePill label="Jita" value={jitaTotal} />
          <span
            className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${verdictBadgeClasses(verdict)}`}
          >
            {verdictLabel(verdict)}
          </span>
        </div>
      </summary>
      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-1">
        <Row label="Jita sell (per unit)" value={fmt(item.jita.sell_price)} />
        <Row label="Jita total" value={fmt(jitaTotal)} bold />
      </div>
    </details>
  )
}

function PricePill({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="text-right">
      <div className="text-[10px] text-eve-muted uppercase font-mono">{label}</div>
      <div className="text-sm font-mono">
        {value !== null ? formatISKShort(value) : '—'}
      </div>
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-2 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function fmt(n: number | null): string {
  return n !== null ? formatISK(n, 2) : '—'
}
